import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import {
  DiscrepancyJob,
  getDiscrepancyJobs,
  getUnbilledWork,
  markWorkUnbillable,
  matchBilledPrice,
  MatchBilledPriceRequest,
  MarkUnbillableRequest,
  resolveInvoicedLines,
  ResolveInvoicedLinesRequest,
  UnbilledWorkRequest,
  UnbilledWorkResult,
} from './markUnbillableApi'

const unbilledWorkKey = (request: UnbilledWorkRequest | null) => {
  return ['admin', 'mark-unbillable', request?.tenantId ?? 0, request?.jobId ?? 0]
}

const discrepancyJobsKey = (tenantId: number | undefined) => {
  return ['admin', 'mark-unbillable', 'jobs', tenantId ?? 0]
}

export const useDiscrepancyJobsQuery = (tenantId: number | undefined, enabled: boolean) => {
  return useQuery<DiscrepancyJob[], AxiosError>(discrepancyJobsKey(tenantId), {
    queryFn: () => getDiscrepancyJobs(tenantId as number),
    enabled: enabled && tenantId !== undefined,
    refetchOnWindowFocus: false,
  })
}

export const useUnbilledWorkQuery = (request: UnbilledWorkRequest | null) => {
  return useQuery<UnbilledWorkResult, AxiosError>(unbilledWorkKey(request), {
    queryFn: () => getUnbilledWork(request as UnbilledWorkRequest),
    enabled: request !== null,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export const useMarkUnbillableMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, MarkUnbillableRequest>({
    mutationFn: (request: MarkUnbillableRequest) => markWorkUnbillable(request),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries(unbilledWorkKey(request))
      queryClient.invalidateQueries(discrepancyJobsKey(request.tenantId))
    },
  })
}

export const useResolveInvoicedLinesMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, ResolveInvoicedLinesRequest>({
    mutationFn: (request: ResolveInvoicedLinesRequest) => resolveInvoicedLines(request),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries(unbilledWorkKey(request))
      queryClient.invalidateQueries(discrepancyJobsKey(request.tenantId))
    },
  })
}

export const useMatchBilledPriceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError, MatchBilledPriceRequest>({
    mutationFn: (request: MatchBilledPriceRequest) => matchBilledPrice(request),
    onSuccess: (_, request) => {
      queryClient.invalidateQueries(unbilledWorkKey(request))
      queryClient.invalidateQueries(discrepancyJobsKey(request.tenantId))
    },
  })
}
